import { NumberInput } from "@internal/ui";
import { Check, ChevronLeft, Lock, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { JobLogModal } from "@/components/ui/JobLogModal";
import { Pill } from "@/components/ui/Pill";
import { WeekSummaryCard } from "@/components/ui/WeekSummaryCard";
import { useObservable } from "@/hooks/useObservable";
import { formatKr } from "@/lib/format";
import { loggedDateOf } from "@/lib/loggedDate";
import {
  formatDateOnly,
  formatDayHeading,
  formatDayTabLabel,
  parseDateOnly,
  weekWithLabel,
} from "@/lib/week";
import {
  computeWeekFinancials,
  findPriorValues,
  sumLogAmounts,
} from "@/lib/weekFinancials";
import { familyService } from "@/services/family.service";
import { jobsService } from "@/services/jobs.service";
import {
  type ChildWeekAggregate,
  parentWeekService,
} from "@/services/parentWeek.service";
import type {
  JobLogData,
  JobLogRecord,
  JobRecord,
  WeekRecord,
  WeekStatus,
} from "@/types/domain";

interface LocalLog {
  tempId: string;
  job: JobRecord;
  amount: number;
  note: string;
  loggedTime: string;
  loggedDate: string;
  createdAt: string;
}

interface ChildEditState {
  active: boolean;
  pendingAdds: LocalLog[];
  pendingDeletes: Set<string>;
  bankAfter: number | null;
  stockAfter: number | null;
  totalInvested: number | null;
  totalInvestedInitialized: boolean;
}

function defaultEditState(): ChildEditState {
  return {
    active: false,
    pendingAdds: [],
    pendingDeletes: new Set(),
    bankAfter: null,
    stockAfter: null,
    totalInvested: null,
    totalInvestedInitialized: false,
  };
}

function todayHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function ParentWeekDetail() {
  const params = useParams<{ yearWeek: string }>();
  const [yearStr, weekStr] = (params.yearWeek ?? "").split("-");
  const year = Number(yearStr);
  const isoWeek = Number(weekStr);

  const navigate = useNavigate();
  const row = useObservable(
    useMemo(() => parentWeekService.weekRow$(year, isoWeek), [year, isoWeek]),
    null,
  );

  // Track unsaved edits across all child sections. Map keyed by child.id.
  const [editStates, setEditStates] = useState<Record<string, ChildEditState>>(
    {},
  );

  const hasUnsavedEdits = useMemo(
    () =>
      Object.values(editStates).some(
        (s) =>
          s.active && (s.pendingAdds.length > 0 || s.pendingDeletes.size > 0),
      ),
    [editStates],
  );

  // Browser-level guard (refresh / tab close)
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedEdits]);

  // In-app navigation guard
  const blocker = useBlocker(hasUnsavedEdits);

  function setEditState(
    childId: string,
    update: (prev: ChildEditState) => ChildEditState,
  ) {
    setEditStates((prev) => ({
      ...prev,
      [childId]: update(prev[childId] ?? defaultEditState()),
    }));
  }

  if (!row) {
    return (
      <ParentLayout>
        <div style={{ color: "var(--ink-3)", fontSize: 14 }}>Laster…</div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div style={{ maxWidth: 720 }}>
        <button
          type="button"
          data-testid="back-to-weeks"
          onClick={() => navigate("/parent/weeks")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--ink-3)",
            background: "transparent",
            marginBottom: 12,
          }}
        >
          <ChevronLeft size={14} color="var(--ink-3)" /> Tilbake til uker
        </button>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              fontWeight: 700,
            }}
          >
            {row.weekRangeLabel}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 34,
              fontWeight: 400,
              marginTop: 2,
              letterSpacing: -0.8,
            }}
          >
            Uke {isoWeek}, {year}
          </div>
          {row.lengthDays !== 7 && (
            <div style={{ marginTop: 8 }}>
              <Pill tone="butter" data-testid="transition-week-badge">
                Overgangsuke · {row.lengthDays} dager
              </Pill>
            </div>
          )}
        </div>

        <div
          data-testid="child-sections"
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          {row.children.map((aggregate) => (
            <ChildSection
              key={aggregate.child.id}
              aggregate={aggregate}
              year={year}
              isoWeek={isoWeek}
              weekStart={row.startDate}
              weekEnd={row.endDate}
              editState={editStates[aggregate.child.id] ?? defaultEditState()}
              setEditState={(update) =>
                setEditState(aggregate.child.id, update)
              }
            />
          ))}
        </div>
      </div>

      {blocker.state === "blocked" && (
        <ConfirmDialog
          title="Ulagrede endringer"
          message="Du har endringer som ikke er lagret. Vil du forlate siden?"
          confirmLabel="Forlat"
          onConfirm={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />
      )}
    </ParentLayout>
  );
}

function ChildSection({
  aggregate,
  year,
  isoWeek,
  weekStart,
  weekEnd,
  editState,
  setEditState,
}: {
  aggregate: ChildWeekAggregate;
  year: number;
  isoWeek: number;
  weekStart: string | null;
  weekEnd: string | null;
  editState: ChildEditState;
  setEditState: (update: (prev: ChildEditState) => ChildEditState) => void;
}) {
  const { child, week, status, logs } = aggregate;
  const jobs = useObservable(jobsService.jobs$, []);
  const allChildWeeks = useObservable(
    useMemo(() => parentWeekService.childWeeks$(child.id), [child.id]),
    [] as WeekRecord[],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [modalJob, setModalJob] = useState<JobRecord | null>(null);
  const [saving, setSaving] = useState(false);

  // Days of the viewed week, driving the picker's day tabs and the
  // day-grouped log list. Bounds come from the viewed row; synthesized from
  // the family offset as a last resort.
  const weekDays = useMemo(() => {
    const bounds =
      weekStart && weekEnd
        ? { start: parseDateOnly(weekStart), end: parseDateOnly(weekEnd) }
        : weekWithLabel(isoWeek, year, familyService.weekStartDay());
    const days: Date[] = [];
    for (
      const d = new Date(bounds.start);
      d <= bounds.end;
      d.setDate(d.getDate() + 1)
    ) {
      days.push(new Date(d));
    }
    return days;
  }, [weekStart, weekEnd, isoWeek, year]);

  const todayStr = formatDateOnly(new Date());
  const dayStrs = weekDays.map(formatDateOnly);
  const lastDayStr = dayStrs[dayStrs.length - 1] ?? todayStr;
  // Default picked day: today when the viewed week contains it, else the
  // week's last day for past weeks and first day for future weeks.
  const defaultDay = dayStrs.includes(todayStr)
    ? todayStr
    : todayStr > lastDayStr
      ? lastDayStr
      : (dayStrs[0] ?? todayStr);
  const [pickedDay, setPickedDay] = useState(defaultDay);

  const isClosedEditing = status === "closed" && editState.active;
  const canEditLogs =
    status === "open" || status === "child_confirmed" || isClosedEditing;
  const canCloseDirectly = status === "child_confirmed";

  const priorValues = useMemo(
    () => findPriorValues(allChildWeeks, child.id, isoWeek, year),
    [allChildWeeks, child.id, isoWeek, year],
  );

  // Visible logs: filter out pending deletes (only relevant when closed-editing)
  const visibleDbLogs = logs.filter((l) => !editState.pendingDeletes.has(l.id));
  const displayedTotal =
    visibleDbLogs.reduce((s, l) => s + l.data.amount, 0) +
    editState.pendingAdds.reduce((s, l) => s + l.amount, 0);

  // Saved and pending logs grouped by day, chronological. Only days that
  // have logs get a group.
  type DayGroupRow =
    | { kind: "db"; log: JobLogRecord }
    | { kind: "pending"; local: LocalLog };
  const groupsByDay = new Map<string, DayGroupRow[]>();
  for (const log of visibleDbLogs) {
    const day = loggedDateOf(log);
    groupsByDay.set(day, [
      ...(groupsByDay.get(day) ?? []),
      { kind: "db", log },
    ]);
  }
  for (const local of editState.pendingAdds) {
    groupsByDay.set(local.loggedDate, [
      ...(groupsByDay.get(local.loggedDate) ?? []),
      { kind: "pending", local },
    ]);
  }
  const dayGroups = [...groupsByDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, rows]) => ({ day, rows }));
  const totalRowCount = dayGroups.reduce((s, g) => s + g.rows.length, 0);

  // Bank/stock/total_invested controlled values
  const showInputs = canCloseDirectly || isClosedEditing;
  const bankValue = showInputs ? editState.bankAfter : null;
  const stockValue = showInputs ? editState.stockAfter : null;
  // `Total investert` prefills from the most-recent prior week with a value
  // set, until the parent types into it (totalInvestedInitialized flips on
  // first onChange). Reopening a closed week initialises directly from the
  // saved value in handleReopen.
  const totalInvestedValue = !showInputs
    ? null
    : editState.totalInvestedInitialized
      ? editState.totalInvested
      : priorValues.totalInvestedBefore;

  const allInputsValid =
    bankValue != null && stockValue != null && totalInvestedValue != null;

  const liveFinancials = computeWeekFinancials({
    earned: displayedTotal,
    bankAfter: bankValue,
    stockAfter: stockValue,
    totalInvested: totalInvestedValue,
    bankBefore: priorValues.bankBefore,
    stockBefore: priorValues.stockBefore,
    totalInvestedBefore: priorValues.totalInvestedBefore,
  });

  const closedFinancials =
    status === "closed" && week
      ? computeWeekFinancials({
          earned: sumLogAmounts(logs),
          bankAfter: week.data.bank_balance_after,
          stockAfter: week.data.stock_value_after,
          totalInvested: week.data.total_invested,
          bankBefore: priorValues.bankBefore,
          stockBefore: priorValues.stockBefore,
          totalInvestedBefore: priorValues.totalInvestedBefore,
        })
      : null;

  // Filter job templates: assigned_to includes child OR is empty (unassigned)
  const availableJobs = jobs.filter(
    (j) =>
      j.data.assigned_to.length === 0 || j.data.assigned_to.includes(child.id),
  );

  async function handleLogPicked(
    job: JobRecord,
    note: string,
    amountOverride: number | null,
    day: string,
  ) {
    const amount = amountOverride ?? job.data.price ?? 0;
    if (isClosedEditing) {
      // Buffer locally
      const local: LocalLog = {
        tempId: crypto.randomUUID(),
        job,
        amount,
        note,
        loggedTime: todayHHMM(),
        loggedDate: day,
        createdAt: new Date().toISOString(),
      };
      setEditState((prev) => ({
        ...prev,
        pendingAdds: [...prev.pendingAdds, local],
      }));
    } else {
      await parentWeekService.addLog(child, isoWeek, year, job, {
        note,
        amount,
        logDate: day,
      });
    }
    setModalJob(null);
    setPickerOpen(false);
  }

  function handlePickJob(job: JobRecord, day: string) {
    setPickedDay(day);
    const needsModal = job.data.require_comment || job.data.price === null;
    if (needsModal) {
      setModalJob(job);
    } else {
      handleLogPicked(job, "", null, day);
    }
  }

  async function handleDeleteDbLog(logId: string) {
    if (isClosedEditing) {
      setEditState((prev) => {
        const next = new Set(prev.pendingDeletes);
        next.add(logId);
        return { ...prev, pendingDeletes: next };
      });
    } else {
      await parentWeekService.deleteLog(logId);
    }
  }

  function handleDeletePendingAdd(tempId: string) {
    setEditState((prev) => ({
      ...prev,
      pendingAdds: prev.pendingAdds.filter((l) => l.tempId !== tempId),
    }));
  }

  async function handleConfirmWeek() {
    if (saving) return;
    setSaving(true);
    try {
      await parentWeekService.confirmChildWeek(child, isoWeek, year);
    } finally {
      setSaving(false);
    }
  }

  async function handleCloseChild() {
    if (!week || saving || !allInputsValid) return;
    setSaving(true);
    try {
      await parentWeekService.closeChildWeek(week.id, {
        bank_balance: bankValue!,
        stock_value: stockValue!,
        total_invested: totalInvestedValue!,
      });
    } finally {
      setSaving(false);
    }
  }

  function handleReopen() {
    setEditState(() => ({
      active: true,
      pendingAdds: [],
      pendingDeletes: new Set(),
      bankAfter: week?.data.bank_balance_after ?? null,
      stockAfter: week?.data.stock_value_after ?? null,
      totalInvested: week?.data.total_invested ?? null,
      totalInvestedInitialized: true,
    }));
  }

  async function handleSaveReopen() {
    if (!week || saving || !allInputsValid) return;
    setSaving(true);
    try {
      for (const localLog of editState.pendingAdds) {
        await parentWeekService.addLog(child, isoWeek, year, localLog.job, {
          note: localLog.note,
          amount: localLog.amount,
          loggedTime: localLog.loggedTime,
          logDate: localLog.loggedDate,
        });
      }
      for (const logId of editState.pendingDeletes) {
        await parentWeekService.deleteLog(logId);
      }
      const newBank = bankValue!;
      const newStock = stockValue!;
      const newTotalInvested = totalInvestedValue!;
      const bankChanged = newBank !== (week.data.bank_balance_after ?? null);
      const stockChanged = newStock !== (week.data.stock_value_after ?? null);
      const investedChanged =
        newTotalInvested !== (week.data.total_invested ?? null);
      if (bankChanged || stockChanged || investedChanged) {
        await parentWeekService.closeChildWeek(week.id, {
          bank_balance: newBank,
          stock_value: newStock,
          total_invested: newTotalInvested,
        });
      }
      setEditState(() => defaultEditState());
    } finally {
      setSaving(false);
    }
  }

  function handleCancelReopen() {
    setEditState(() => defaultEditState());
  }

  return (
    <Card
      data-testid="child-section"
      data-child-id={child.id}
      data-status={status}
      style={{ padding: 20 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Avatar person={child} size={42} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{child.data.name}</div>
          <div
            className="num"
            style={{ fontSize: 13, color: "var(--good)", fontWeight: 700 }}
          >
            +{formatKr(displayedTotal)}
          </div>
        </div>
        <StatusPill status={status} editing={isClosedEditing} />
      </div>

      <Card padding={0} style={{ overflow: "hidden", marginBottom: 12 }}>
        {dayGroups.length === 0 ? (
          <div
            style={{
              padding: "16px 20px",
              color: "var(--ink-3)",
              fontSize: 13,
            }}
          >
            Ingen jobber logget.
          </div>
        ) : (
          dayGroups.map((group, groupIndex) => {
            const rowsBefore = dayGroups
              .slice(0, groupIndex)
              .reduce((s, g) => s + g.rows.length, 0);
            return (
              <div
                key={group.day}
                data-testid="day-group"
                data-date={group.day}
              >
                <div
                  data-testid="day-group-header"
                  style={{
                    padding: "8px 16px",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "var(--ink-3)",
                    background: "var(--bg-2)",
                    borderBottom: "1px solid var(--line)",
                  }}
                >
                  {formatDayHeading(parseDateOnly(group.day))}
                </div>
                {group.rows.map((row, i) => {
                  const isLast = rowsBefore + i + 1 === totalRowCount;
                  return row.kind === "db" ? (
                    <DbLogRow
                      key={row.log.id}
                      log={row.log}
                      canDelete={canEditLogs}
                      onDelete={() => handleDeleteDbLog(row.log.id)}
                      isLast={isLast}
                    />
                  ) : (
                    <PendingLogRow
                      key={row.local.tempId}
                      local={row.local}
                      onDelete={() => handleDeletePendingAdd(row.local.tempId)}
                      isLast={isLast}
                    />
                  );
                })}
              </div>
            );
          })
        )}
      </Card>

      {canEditLogs && (
        <div
          style={{ marginBottom: canCloseDirectly || isClosedEditing ? 16 : 0 }}
        >
          <Button
            data-testid="add-log-button"
            variant="soft"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setPickerOpen(true)}
          >
            Legg til jobb
          </Button>
        </div>
      )}

      {status === "open" && todayStr >= lastDayStr && (
        <div style={{ marginTop: 12 }}>
          <Button
            data-testid="confirm-child-week-button"
            variant="soft"
            size="md"
            icon={<Check size={15} />}
            onClick={handleConfirmWeek}
            disabled={saving}
            fullWidth
          >
            {`Bekreft uka for ${child.data.name}`}
          </Button>
        </div>
      )}

      {showInputs && (
        <div data-testid="bank-stock-form" style={{ marginBottom: 14 }}>
          <div style={{ marginBottom: 12 }}>
            <NumberInput
              data-testid="bank-balance-input"
              label="Banksaldo etter"
              value={bankValue}
              evaluateExpression
              onChange={(v) =>
                setEditState((prev) => ({ ...prev, bankAfter: v }))
              }
            />
          </div>
          <div
            data-testid="investering-group"
            style={{
              border: "1px solid var(--line)",
              borderRadius: "var(--r-md)",
              padding: 12,
              background: "var(--bg-2)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "var(--ink-3)",
                marginBottom: 8,
              }}
            >
              Investering
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <NumberInput
                data-testid="stock-value-input"
                label="Fond etter"
                value={stockValue}
                evaluateExpression
                onChange={(v) =>
                  setEditState((prev) => ({ ...prev, stockAfter: v }))
                }
              />
              <NumberInput
                data-testid="total-invested-input"
                label="Total investert"
                value={totalInvestedValue}
                evaluateExpression
                onChange={(v) =>
                  setEditState((prev) => ({
                    ...prev,
                    totalInvested: v,
                    totalInvestedInitialized: true,
                  }))
                }
              />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <WeekSummaryCard
              financials={liveFinancials}
              title="Ukens regnskap"
              data-testid="week-summary-preview"
            />
          </div>
        </div>
      )}

      {canCloseDirectly && (
        <Button
          data-testid="close-child-week-button"
          variant="primary"
          size="md"
          icon={<Lock size={15} color="#fff" />}
          onClick={handleCloseChild}
          disabled={saving || !allInputsValid}
          fullWidth
        >
          {saving ? "Lukker…" : `Lås uka for ${child.data.name}`}
        </Button>
      )}

      {status === "closed" && !isClosedEditing && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {closedFinancials && (
            <WeekSummaryCard
              financials={closedFinancials}
              title="Ukens regnskap"
              data-testid="week-summary-closed"
            />
          )}
          <Button
            data-testid="reopen-week-button"
            variant="ghost"
            size="sm"
            onClick={handleReopen}
          >
            Åpne uka igjen
          </Button>
        </div>
      )}

      {isClosedEditing && (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            data-testid="save-reopen-button"
            variant="primary"
            size="md"
            onClick={handleSaveReopen}
            disabled={saving || !allInputsValid}
          >
            {saving ? "Lagrer…" : "Lagre"}
          </Button>
          <Button
            data-testid="cancel-reopen-button"
            variant="ghost"
            size="md"
            onClick={handleCancelReopen}
            disabled={saving}
          >
            Avbryt
          </Button>
        </div>
      )}

      {pickerOpen && (
        <JobPicker
          jobs={availableJobs}
          days={weekDays}
          defaultDay={defaultDay}
          onPick={(job, day) => {
            setPickerOpen(false);
            handlePickJob(job, day);
          }}
          onCancel={() => setPickerOpen(false)}
        />
      )}

      {modalJob && (
        <JobLogModal
          job={modalJob}
          onSubmit={async ({ note, amount }) => {
            await handleLogPicked(modalJob, note, amount, pickedDay);
          }}
          onCancel={() => setModalJob(null)}
        />
      )}
    </Card>
  );
}

function DbLogRow({
  log,
  canDelete,
  onDelete,
  isLast,
}: {
  log: JobLogRecord;
  canDelete: boolean;
  onDelete: () => void;
  isLast: boolean;
}) {
  return (
    <LogRowShell
      isLast={isLast}
      emoji={log.data.job_snapshot.emoji}
      title={log.data.job_snapshot.title}
      note={log.data.note}
      amount={log.data.amount}
      onDelete={canDelete ? onDelete : null}
      status={log.data.status}
    />
  );
}

function PendingLogRow({
  local,
  onDelete,
  isLast,
}: {
  local: LocalLog;
  onDelete: () => void;
  isLast: boolean;
}) {
  return (
    <LogRowShell
      isLast={isLast}
      emoji={local.job.data.emoji}
      title={local.job.data.title}
      note={local.note}
      amount={local.amount}
      onDelete={onDelete}
      status={"approved" as JobLogData["status"]}
      pending
    />
  );
}

function LogRowShell({
  isLast,
  emoji,
  title,
  note,
  amount,
  onDelete,
  status,
  pending,
}: {
  isLast: boolean;
  emoji: string;
  title: string;
  note: string;
  amount: number;
  onDelete: (() => void) | null;
  status: JobLogData["status"];
  pending?: boolean;
}) {
  const statusColor =
    status === "rejected"
      ? "var(--bad)"
      : status === "approved"
        ? "var(--good)"
        : "var(--ink)";
  return (
    <div
      data-testid="log-row"
      data-status={status}
      data-pending={pending ? "true" : "false"}
      style={{
        padding: "12px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 18 }}>{emoji}</span>
      <div style={{ flex: 1 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            textDecoration: status === "rejected" ? "line-through" : "none",
          }}
        >
          {title}
        </span>
        {note && (
          <div
            data-testid="log-note"
            style={{ fontSize: 12, color: "var(--ink-3)" }}
          >
            {note}
          </div>
        )}
      </div>
      <span
        data-testid="log-amount"
        className="num"
        style={{ fontWeight: 700, fontSize: 14, color: statusColor }}
      >
        {amount >= 0 ? "+" : ""}
        {amount} kr
      </span>
      {onDelete && (
        <button
          type="button"
          data-testid="delete-log-button"
          onClick={onDelete}
          style={{ padding: 4 }}
        >
          <Trash2 size={18} color="var(--bad)" />
        </button>
      )}
    </div>
  );
}

function JobPicker({
  jobs,
  days,
  defaultDay,
  onPick,
  onCancel,
}: {
  jobs: JobRecord[];
  days: Date[];
  defaultDay: string;
  onPick: (job: JobRecord, day: string) => void;
  onCancel: () => void;
}) {
  const [selectedDay, setSelectedDay] = useState(defaultDay);
  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(31, 42, 36, 0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        data-testid="job-picker"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-md)",
          padding: 16,
          // Wide enough for a 7-day tab row; transition weeks may still wrap.
          width: 450,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 22,
            fontWeight: 400,
            marginBottom: 14,
            letterSpacing: -0.5,
            padding: "0 6px",
          }}
        >
          Velg en jobb
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginBottom: 12,
            padding: "0 6px",
          }}
        >
          {days.map((day) => {
            const dayStr = formatDateOnly(day);
            const isSelected = dayStr === selectedDay;
            return (
              <button
                key={dayStr}
                type="button"
                data-testid="day-tab"
                data-date={dayStr}
                data-selected={String(isSelected)}
                onClick={() => setSelectedDay(dayStr)}
                style={{
                  flex: 1,
                  minWidth: 42,
                  padding: "6px 2px",
                  borderRadius: "var(--r-md)",
                  border: isSelected
                    ? "2px solid var(--butter)"
                    : "2px solid transparent",
                  background: isSelected ? "var(--butter)" : "var(--bg-2)",
                  color: isSelected ? "var(--ink)" : "var(--ink-2)",
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {formatDayTabLabel(day)}
              </button>
            );
          })}
        </div>
        {jobs.length === 0 ? (
          <div
            style={{
              padding: 18,
              fontSize: 13,
              color: "var(--ink-3)",
              textAlign: "center",
            }}
          >
            Ingen tilgjengelige jobber.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {jobs.map((job) => (
              <button
                key={job.id}
                type="button"
                data-testid="job-picker-option"
                onClick={() => onPick(job, selectedDay)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 10px",
                  borderRadius: 10,
                  background: "transparent",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 20 }}>{job.data.emoji}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>
                  {job.data.title}
                </span>
                {job.data.price != null && (
                  <span
                    className="num"
                    style={{ fontSize: 13, color: "var(--ink-3)" }}
                  >
                    +{job.data.price} kr
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 8,
            padding: "0 6px",
          }}
        >
          <Button
            data-testid="job-picker-cancel"
            variant="ghost"
            size="sm"
            onClick={onCancel}
          >
            Avbryt
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({
  status,
  editing,
}: {
  status: WeekStatus;
  editing: boolean;
}) {
  if (editing)
    return (
      <Pill tone="butter" size="sm" data-testid="week-status-pill">
        Redigerer
      </Pill>
    );
  if (status === "open")
    return (
      <Pill tone="warn" size="sm" data-testid="week-status-pill">
        Åpen
      </Pill>
    );
  if (status === "child_confirmed")
    return (
      <Pill tone="coral" size="sm" data-testid="week-status-pill">
        Bekreftet
      </Pill>
    );
  return (
    <Pill tone="forest" size="sm" data-testid="week-status-pill">
      Lukket
    </Pill>
  );
}
